import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { useLocaleStore } from '@/i18n';
import { detectDeviceType, sabaNormalizeDeviceType } from '@/lib/deviceType';
import { apiGet, apiPost } from './_base';

export type GameDto = {
  uuid: string;
  name: string;
  /** Chinese-specific game title (per backend feat/game-name-zh-column).
   *  Populated on /casino/game/list, /casino/game/{id}, /casino/game/fav,
   *  /casino/user-history. Empty string when no Chinese name exists; in
   *  that case (and for non-zh locales) consumers fall back to `name`. */
  name_zh?: string;
  type?: string;
  provider?: string;
  provider_id?: string;
  image?: string;
  system_image?: string;
  is_recommended?: boolean;
  is_popular?: boolean;
  featured_type?: string;
  theme_types?: string[];
  priority?: number;
  is_available?: boolean;
  /** 三态状态：ENABLED | MAINTENANCE | DISABLED。MAINTENANCE 在 UI 置灰不可点；
   *  DISABLED 后端对玩家不返回。注意后端对 MAINTENANCE 派生 is_available=false，
   *  所以可见性判断必须优先用本字段。 */
  status?: string;
  /** Server-side per-account gate; `false` games are rejected by /game/load (e.g. hash games for non-permissioned users). */
  is_allowed?: boolean;
  /** Canonical category signal on /casino/game/list ('' for HG hash, vendor name otherwise).
   *  ⚠ /casino/game/{id} always returns '' regardless of vendor — use isHashGame() instead of trusting this field on lookups. */
  game_aggregator?: string;
  /** '888BIT' for HG in-house hash games — set on both /list and /game/{id}. Use to detect hash games reliably. */
  technology?: string;
};

// Reliable across BOTH /casino/game/list and /casino/game/{id}. The
// per-game GET response zeros out game_aggregator for every vendor (backend
// quirk), so checking !game_aggregator there misclassifies vendor games as
// hash. technology and type stay populated, so we key on those.
export function isHashGame(g: Pick<GameDto, 'technology' | 'type'> | undefined | null): boolean {
  if (!g) return false;
  return g.technology === '888BIT' || g.type === 'ORIGINAL_GAMES';
}

export type ProviderDto = {
  name: string;
  provider_id: string;
  count: string;
  image: string;
  priority: number;
};

export type GameListResponse = {
  list_games: GameDto[];
  providers: ProviderDto[];
  theme_types: string[];
  pagination_info: {
    page_number: number;
    page_size: number;
    total_pages: number;
    total_items: number;
  };
};

// Full catalog fetch (single page, generous size). Used to populate home
// blocks and to feed the Game page's per-tab grouping. Renamed from
// useGameListQuery to free that name for game.ts's per-provider
// infinite-scroll hook.
//
// page_size defaults to 1000 because the home page filters one fetch into
// six per-aggregator buckets (live, slots, hash, sport, fishing, card) and
// any cap that drops games per category leaves visibly-empty sections.
// If the catalog grows past this size, switch to a paginated accumulate
// rather than bumping further.
//
// providerName: when provided the server filters the list to that provider,
// keeping the request small and ensuring the provider name is present in the
// API call (e.g. ?provider_name=DBCARD). Callers that need the full catalog
// (home page, game-tab grid) omit this parameter.
export function useGameCatalogQuery(pageSize = 1000, providerName?: string) {
  const params = new URLSearchParams({ page: '1', page_size: String(pageSize) });
  if (providerName) params.set('providers', providerName);
  return useQuery({
    queryKey: ['casino', 'games', pageSize, providerName ?? ''],
    queryFn: () =>
      apiGet<GameListResponse>(`/api/v1/casino/game/list?${params.toString()}`),
  });
}

// === Home "Continue play / Favorites" tab-row =============================
//
// Backend contract per /api/v1/casino/docs:
//   GET /api/v1/casino/user-history → recently played games (Continue play)
//   GET /api/v1/casino/game/fav     → user's favorite-marked games
// Both return ListGames items the same shape used by the catalog, so callers
// can render them via the same GameCard / BlockPreview UI.
//
// Both hooks are gated on `enabled` so they don't fire for logged-out users
// (the Home page passes `enabled: isAuthed`). Auth-state changes invalidate
// these queries elsewhere.

// Wire shape diverges between the two endpoints: user-history serialises
// the games array as `list_games`, while game/fav uses `games`. Mirror
// each here so neither hook silently reads from an undefined field.
export type UserHistoryResponse = { list_games?: GameDto[] };
export type FavoriteGamesResponse = { games?: GameDto[] };

export function useUserHistoryQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['casino', 'user-history'],
    enabled: options?.enabled ?? true,
    queryFn: () => apiGet<UserHistoryResponse>('/api/v1/casino/user-history'),
    select: (res) => res.list_games ?? [],
  });
}

export function useFavoriteGamesQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['casino', 'game-fav'],
    enabled: options?.enabled ?? true,
    queryFn: () => apiGet<FavoriteGamesResponse>('/api/v1/casino/game/fav'),
    select: (res) => res.games ?? [],
  });
}

// Toggle favorite — POST /api/v1/casino/user-favorite
// Body: { game_uuid, update_favorite }. Backend echoes the resulting state
// in `favorite_updated`; we trust that to drive the optimistic cache update.
//
// Pattern: optimistically add/remove the game from the cached favorites list
// for instant heart-icon feedback, then invalidate to reconcile with whatever
// the server returns on the next /game/fav read.
type SetFavoriteResp = { favorite_updated?: boolean };

export function useSetFavoriteGameMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ gameUuid, favorite }: { gameUuid: string; favorite: boolean }) =>
      apiPost<SetFavoriteResp>('/api/v1/casino/user-favorite', {
        game_uuid: gameUuid,
        update_favorite: favorite,
      }),
    onMutate: async ({ gameUuid, favorite }) => {
      await qc.cancelQueries({ queryKey: ['casino', 'game-fav'] });
      const prev = qc.getQueryData<FavoriteGamesResponse>(['casino', 'game-fav']);
      qc.setQueryData<FavoriteGamesResponse>(['casino', 'game-fav'], (old) => {
        const games = old?.games ?? [];
        if (favorite) {
          if (games.some((g) => g.uuid === gameUuid)) return old ?? { games };
          // The catalog cache usually carries the full GameDto for this uuid —
          // pull it so the optimistic entry has the same shape, not a stub.
          const cached = qc
            .getQueriesData<GameListResponse>({ queryKey: ['casino', 'games'] })
            .map(([, data]) => data?.list_games?.find((g) => g.uuid === gameUuid))
            .find(Boolean);
          const stub: GameDto = cached ?? { uuid: gameUuid, name: '' };
          return { games: [stub, ...games] };
        }
        return { games: games.filter((g) => g.uuid !== gameUuid) };
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['casino', 'game-fav'], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['casino', 'game-fav'] });
    },
  });
}

// Provider list — currently sourced from the same /casino/game/list response
// that already populates the home and Game pages, so this is free when the
// catalog cache is warm. Falls back to a tiny fetch (page_size=1) when cold.
export function useProvidersQuery() {
  const qc = useQueryClient();
  const cached = qc
    .getQueriesData<GameListResponse>({ queryKey: ['casino', 'games'] })
    .map(([, data]) => data?.providers)
    .find((p): p is ProviderDto[] => Array.isArray(p) && p.length > 0);

  return useQuery({
    queryKey: ['casino', 'providers'],
    enabled: !cached,
    initialData: cached
      ? ({
          providers: cached,
          // Minimum fields needed to satisfy the GameListResponse contract — only
          // the providers array is consumed.
          list_games: [],
          theme_types: [],
          pagination_info: { page_number: 1, page_size: 1, total_pages: 0, total_items: 0 },
        } satisfies GameListResponse)
      : undefined,
    queryFn: () => apiGet<GameListResponse>('/api/v1/casino/game/list?page=1&page_size=1'),
    select: (res) => res.providers ?? [],
  });
}

// Look up a single game by uuid. Reads from the list-query cache when warm
// (typical case: navigated from the home page); falls back to GET /casino/game/{id}.
// Returned shape matches GameDto so callers can branch on game_aggregator.
export function useGameInfoQuery(uuid: string) {
  const qc = useQueryClient();
  const cached = qc
    .getQueriesData<GameListResponse>({ queryKey: ['casino', 'games'] })
    .map(([, data]) => data?.list_games?.find((g) => g.uuid === uuid))
    .find(Boolean);

  return useQuery({
    queryKey: ['casino', 'game', uuid],
    enabled: Boolean(uuid) && !cached,
    initialData: cached,
    queryFn: () => apiGet<GameDto>(`/api/v1/casino/game/${encodeURIComponent(uuid)}`),
  });
}

export type LoadGameResponse = { url: string };

export type HashGameConfig = {
  chain: string;
  gameId: string;
  beginnerAddress: string;
  beginnerBetMin: string;
  beginnerBetMax: string;
  beginnerWin: string;
  intermediateAddress: string;
  intermediateBetMin: string;
  intermediateBetMax: string;
  intermediateWin: string;
  counter: string;
};

export type HashResult = {
  id: string;
  result: number;
  chain_type: string;
  block_number: number;
  block_hash: string;
  created_at: string;
};

export type InstantHashResults = {
  current_block: string;
  next_block: string;
  draw_block: string;
  count: number;
  evenBetAmount?: string;
  oddBetAmount?: string;
  hash_results: HashResult[];
};

export function useHashGameConfigQuery(gameId: string, chain = 'TRON') {
  return useQuery({
    queryKey: ['casino', 'hash-config', chain, gameId],
    enabled: Boolean(gameId),
    queryFn: () =>
      apiGet<HashGameConfig>(
        `/api/v1/casino/game/hash-game-config?chain=${chain}&gameId=${encodeURIComponent(gameId)}`,
      ),
  });
}

// The five hash game variants offered by HG. Result-calculation formulas
// mirror the canonical d.AN config in Nova's frontend (chunk 7280):
//   EVEN_ODD   — last digit of the block-hash decimal result; ODD if odd, EVEN if even
//   BIG_SMALL  — last digit; BIG if >5 else SMALL
//   BANKER     — last 5 hex chars: sum of [0..3) vs sum of [2..5); higher wins
//   BULL_FIGHT — same calc as BANKER but auto-bets one side (server-side rules)
//   LUCKY_HASH — last 2 chars: WIN if exactly one is digit and the other is letter
export type HashGameType = 'EVEN_ODD' | 'BIG_SMALL' | 'BANKER' | 'BULL_FIGHT' | 'LUCKY_HASH';
export type HashGameTiming = 'INSTANT' | 'ONE_MINUTE' | 'THREE_MINUTE';

const HASH_GAME_TYPES: HashGameType[] = ['EVEN_ODD', 'BIG_SMALL', 'BANKER', 'BULL_FIGHT', 'LUCKY_HASH'];

export function getHashGameType(gameId: string): HashGameType {
  return HASH_GAME_TYPES.find((t) => gameId.includes(t)) ?? 'EVEN_ODD';
}

export function getHashGameTiming(gameId: string): HashGameTiming {
  if (gameId.includes('THREE_MINUTE')) return 'THREE_MINUTE';
  if (gameId.includes('ONE_MINUTE')) return 'ONE_MINUTE';
  return 'INSTANT';
}

// Block cadence: TRON makes a block every ~3s, ONE_MINUTE settles on the
// next 60s-aligned block, THREE_MINUTE on the next 180s-aligned block. Used
// only for client-side polling cadence — actual draw block comes from the
// server's `draw_block` field.
export function hashGamePollMs(gameId: string): number {
  const t = getHashGameTiming(gameId);
  return t === 'INSTANT' ? 4_000 : t === 'ONE_MINUTE' ? 10_000 : 20_000;
}

export function useHashGameResultsQuery(gameId: string, chain = 'TRON', pageSize = 20) {
  return useQuery({
    queryKey: ['casino', 'hash-results', chain, gameId, pageSize],
    enabled: Boolean(gameId),
    // Block-driven — cadence varies per game timing (instant ≈ 3s blocks).
    refetchInterval: hashGamePollMs(gameId),
    refetchIntervalInBackground: false,
    queryFn: () =>
      apiGet<InstantHashResults>(
        `/api/v1/casino/game/instant-hash-results?chain=${chain}&gameId=${encodeURIComponent(gameId)}&page_number=1&page_size=${pageSize}`,
      ),
  });
}

// All five game types accept the bets below. BULL_FIGHT and LUCKY_HASH
// require no user selection — Nova hard-codes bet_type='bull_fight' for
// them server-side. The other three accept the listed picks.
export type HashBetType =
  | 'ODD' | 'EVEN'
  | 'BIG' | 'SMALL'
  | 'BANKER' | 'PLAYER' | 'TIE'
  | 'bull_fight';

export type HashBetLevel = 'beginner' | 'intermediate';

export type HashOrderRequest = {
  /** Chain identifier (TRON or BSC). Sent over the wire but currently
   *  dropped by the backend's InstantHashRequest (no chain field there);
   *  the backend reads the same value from `game_type` instead. */
  chain: string;
  game_id: string;
  /** Despite the name, the backend's `GameType` field carries the chain
   *  (TRON / BSC), not the game variant. Pass the chain string here.
   *  The game variant is conveyed via `game_id`. */
  game_type: string;
  bet_type: HashBetType;
  amount: number;
  request_type: HashBetTier;
  currency: string;
};

/**
 * Hash-game bet tier encoded as a small numeric the backend understands:
 *   1 = beginner chip rack (1–100)
 *   2 = intermediate chip rack (100–2000)
 * Use `REQUEST_TYPE[level]` instead of inlining `1` / `2` so a future tier
 * change (or third level) doesn't require grepping for magic numbers.
 */
export type HashBetTier = 1 | 2;
export const REQUEST_TYPE: Record<HashBetLevel, HashBetTier> = {
  beginner: 1,
  intermediate: 2,
};

export type HashOrderResponse = {
  // Server returns an opaque order id and the block_number it's settled on.
  // Schema is sparsely documented — keep flexible.
  order_id?: string;
  id?: string;
  block_number?: number;
  status?: string;
};

// Place a hash-game bet via the REST endpoint. (Nova's web client uses a
// WebSocket carrying the same payload under type='place_bet_game'; the REST
// path accepts the identical body and is sufficient for our needs.)
export function useHashGameOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: HashOrderRequest) =>
      apiPost<HashOrderResponse>('/api/v1/casino/game/instant-hash-order', req),
    onSuccess: () => {
      // Balance and bet list both change on a successful bet.
      qc.invalidateQueries({ queryKey: ['user', 'balance'] });
      qc.invalidateQueries({ queryKey: ['user', 'bets'] });
    },
  });
}

export type UserBetDto = {
  id: string;
  game_id?: string;
  game_uuid?: string;
  game_name?: string;
  bet_amount?: number;
  win_amount?: number;
  payout?: number;
  status?: string;
  bet_type?: string;
  block_number?: number;
  created_at?: string;
};

export type UserBetsResponse = {
  bets: UserBetDto[];
  pagination_info: {
    page_number: number;
    page_size: number;
    total_pages: number;
    total_items: number;
  };
};

export function useUserBetsQuery(gameId?: string, pageSize = 10) {
  return useQuery({
    queryKey: ['user', 'bets', gameId ?? 'all', pageSize],
    refetchInterval: 8_000,
    refetchIntervalInBackground: false,
    queryFn: () => {
      const qs = new URLSearchParams({ page_number: '1', page_size: String(pageSize) });
      if (gameId) qs.set('game_id', gameId);
      return apiGet<UserBetsResponse>(`/api/v1/user/bets?${qs.toString()}`);
    },
  });
}

// Body for POST /casino/game/load. Server requires every field — no field has
// a server-side default — so we always send the full body.
function buildLoadGameBody(
  gameId: string,
  locale: string,
  username: string,
  provider?: string,
  refUrl?: string,
) {
  const raw = detectDeviceType();
  // SABASports only accepts device_type 1 (PC) or 2 (H5/mobile) — iOS (3),
  // Android (4), and Other (5) must be normalised to 2.
  const device_type = provider === 'SABASports' ? sabaNormalizeDeviceType(raw) : raw;
  return {
    game_uuid: gameId,
    language: locale.split('-')[0],
    // The casino has no fiat wallet — only crypto (USDT/BTC/BNB/ETH/...).
    // 'USD' was being silently accepted by /game/load but caused vendors
    // (PGSoft / FUN100 / 666789) to reject the player session at iframe
    // load time. USDT is universally available across user wallets.
    currency: 'USDT',
    user_currency: 'USDT',
    exchange_rate: '1',
    // 1=PC Web, 2=Mobile Web, 3=iOS, 4=Android, 5=Other. Backend's
    // /casino/game/load maps this per-aggregator (DBLive consumes the
    // enum directly; DBFishing / PGSoft / PPLive get translated).
    device_type,
    player_name: username,
    // The server allowlists return_url and rejects unknown origins (e.g.
    // http://localhost:5173) with 5062 "Internal server error occurred".
    // Use the API host as a known-good value until the prod app domain is
    // registered server-side.
    return_url: import.meta.env.VITE_API_BASE_URL || 'https://api.tt6.com',
    lobby_data: '',
    show_exit: 1,
    // h99 launches top-level (new tab) and needs a back-to-site `ref` so the
    // vendor can return the player to our platform. It rides its OWN dedicated
    // field — never return_url, which other aggregators forward as their own
    // back/lobby URL. Empty for every non-h99 game.
    ref_url: refUrl ?? '',
  };
}

// In-flight de-duplication for POST /casino/game/load.
//
// A single game tap fires two loads near-simultaneously: useRecordGamePlay
// (history recorder) on the card, then useLoadGameQuery on the Play page
// mount. Issued concurrently for the same member they race the sports vendor
// (e.g. CR Sports / 皇冠体育), which rejects concurrent LaunchGame calls for one
// member with respcode 0102 — surfaced to the user as an HTTP 500. Coalescing
// identical bodies collapses them into a single vendor launch and one shared
// url, so only one request hits the vendor. Also absorbs accidental double
// taps. The entry clears as soon as the request settles.
const inflightLoadGame = new Map<string, Promise<LoadGameResponse>>();

function postLoadGame(body: ReturnType<typeof buildLoadGameBody>): Promise<LoadGameResponse> {
  const key = JSON.stringify(body);
  const existing = inflightLoadGame.get(key);
  if (existing) return existing;
  const p = apiPost<LoadGameResponse>('/api/v1/casino/game/load', body).finally(() => {
    inflightLoadGame.delete(key);
  });
  inflightLoadGame.set(key, p);
  return p;
}

export function useLoadGameQuery(gameId: string, provider?: string, refUrl?: string) {
  const username = useAuthStore((s) => s.user?.username ?? '');
  const locale = useLocaleStore((s) => s.locale);
  const qc = useQueryClient();
  return useQuery({
    queryKey: ['casino', 'game-load', gameId, locale, username, refUrl],
    enabled: Boolean(gameId && username),
    // Game launch URLs are short-lived (signed + ttl in the URL), so don't
    // hold the cached value across navigations.
    gcTime: 0,
    staleTime: 0,
    queryFn: async () => {
      const data = await postLoadGame(buildLoadGameBody(gameId, locale, username, provider, refUrl));
      // /game/load writes a LoadGameRequest row, which is the source for
      // Continue Play — refresh that list so the just-launched game pops to
      // the front next time Home reads it.
      qc.invalidateQueries({ queryKey: ['casino', 'user-history'] });
      return data;
    },
  });
}

// Fire-and-forget "I clicked this game" recorder. /casino/user-history is
// sourced from LoadGameRequest rows, so calling /game/load is the canonical
// way to make a game show up in Continue Play. Caller passes the game uuid;
// the hook gates on auth (no-op when logged out) and on a non-empty gameId.
// Errors are swallowed — recording is non-critical and shouldn't block
// navigation. The Continue Play list is invalidated on success so the just-
// tapped game pops to the front next time the Home page reads it.
//
// Note: this also issues a vendor session token (the LoadGameResponse url).
// We discard the url here; vendor games will fetch a fresh one on Play page
// mount via useLoadGameQuery. Hash games may return 5058 — that's expected
// and the swallowed error is fine since /game/load still writes the history
// row before vendor dispatch.
export function useRecordGamePlay() {
  const username = useAuthStore((s) => s.user?.username ?? '');
  const locale = useLocaleStore((s) => s.locale);
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (gameId: string) =>
      postLoadGame(buildLoadGameBody(gameId, locale, username)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['casino', 'user-history'] });
    },
  });
  return {
    record: (gameId: string) => {
      if (!username || !gameId) return;
      mutation.mutate(gameId);
    },
  };
}
